import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { auth, db, collection, query, where, getDocs, doc, updateDoc } from '../firebase';
import { messaging, getToken } from '../firebase';
import './selfreflection.css';

export default function OftenJournalSetting() {
    const navigate = useNavigate();
    const [journalFreq, setJournalFreq] = useState('Once in 3 days');
    const [showFreqSetting, setShowFreqSetting] = useState(true);
    const [hasSavedSetting, setHasSavedSetting] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [selectedDate, setSelectedDate] = useState(today.getDate());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth()); // 0-indexed
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [isEditingDate, setIsEditingDate] = useState(false);
    const [manualDateInput, setManualDateInput] = useState('');
    const [journaledDates, setJournaledDates] = useState(new Set());

    const context = useOutletContext();
    const user = context?.user;
    const setIsLoginOpen = context?.setIsLoginOpen;

    const handleFreqSelect = async (freq) => {
        setJournalFreq(freq);

        const currentUser = user || auth.currentUser;
        if (!currentUser) {
            if (setIsLoginOpen) setIsLoginOpen(true);
            return;
        }

        try {
            let fcmToken = null;
            if (messaging) {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    // Explicitly register service worker and pass it to getToken
                    try {
                        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                        fcmToken = await getToken(messaging, {
                            vapidKey: 'BJ0XD3Lj-agzfA18CtInj0PIVm4jv2nR8xZfvn89_Ii9vlELVG9D88-IiILjDDNjy0hRRJySH22iYa4-5A_Ob6Q',
                            serviceWorkerRegistration: registration
                        });
                    } catch (err) {
                        console.error('Error getting FCM token', err);
                        fcmToken = null;
                    }
                }
            }

            // Call Cloud Function API
            const apiUrl = "https://us-central1-nabi-1708b.cloudfunctions.net/updateTimeLimit";
            const payload = {
                email: currentUser.email,
                new_time_limit: freq
            };
            if (fcmToken) {
                payload.fcm_token = fcmToken;
            }
            //  console.log(fcmToken,"print fcm token")

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            //  console.log(response,"print resposne here in self reflection")
            if (!response.ok) {
                console.error("Failed to update time limit API");
            } else {
                setHasSavedSetting(true);
                setShowFreqSetting(false);
            }
        } catch (error) {
            console.error("Error updating user preferences:", error);
        }
    };

    React.useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = user || auth.currentUser;
            if (!currentUser) return;

            try {
                const q = query(collection(db, "users"), where("email", "==", currentUser.email));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data();
                    if (data.journalFreq) {
                        setJournalFreq(data.journalFreq);
                        setHasSavedSetting(true);
                        setShowFreqSetting(false);
                    } else {
                        setShowFreqSetting(true);
                    }
                } else {
                    setShowFreqSetting(true);
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };
        fetchUserData();
    }, [user, auth.currentUser]);

    React.useEffect(() => {
        const fetchJournaledDates = async () => {
            const currentUser = user || auth.currentUser;
            if (!currentUser) return;

            try {
                const journalsRef = collection(db, "journals");
                const q = query(journalsRef, where("email", "==", currentUser.email));
                const snapshot = await getDocs(q);

                const datesSet = new Set();
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.entry_time) {
                        // entry_time usually starts with YYYY-MM-DD
                        const ymd = data.entry_time.substring(0, 10);
                        datesSet.add(ymd);
                    }
                });
                setJournaledDates(datesSet);
            } catch (error) {
                console.error("Error fetching journaled dates:", error);
            }
        };
        fetchJournaledDates();
    }, [user, auth.currentUser, isCalendarOpen]);

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(y => y - 1);
        } else {
            setCurrentMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(y => y + 1);
        } else {
            setCurrentMonth(m => m + 1);
        }
    };

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDay = new Date(currentYear, currentMonth, 1).getDay(); // 0 = Sunday

    // If selectedDate exceeds days in the month (e.g. 31 in Feb), fallback to last day
    const validSelectedDate = Math.min(selectedDate, daysInMonth);
    const selectedDateObj = new Date(currentYear, currentMonth, validSelectedDate);

    // Format "Thu, Jun 12" without a trailing comma after Thursday on some browsers
    // We'll format manually or use options
    const formattedDate = selectedDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const monthName = new Date(currentYear, currentMonth).toLocaleDateString('en-US', { month: 'long' });

    const startEditing = () => {
        setManualDateInput(`${String(currentMonth + 1).padStart(2, '0')}/${String(validSelectedDate).padStart(2, '0')}/${currentYear}`);
        setIsEditingDate(true);
    };

    const handleTextInput = (e) => {
        let val = e.target.value.replace(/\D/g, ''); // strip non-digits

        if (val.length > 8) {
            val = val.substring(0, 8);
        }

        let formatted = val;

        // Validate Month
        if (val.length >= 2) {
            let month = parseInt(val.substring(0, 2), 10);
            if (month > 12) month = 12;
            if (month === 0) month = 1;
            val = String(month).padStart(2, '0') + val.substring(2);
            formatted = val.substring(0, 2);
        }

        // Validate Day
        if (val.length >= 4) {
            let day = parseInt(val.substring(2, 4), 10);
            if (day > 31) day = 31;
            if (day === 0) day = 1;
            val = val.substring(0, 2) + String(day).padStart(2, '0') + val.substring(4);
            formatted = val.substring(0, 2) + '/' + val.substring(2, 4);
        } else if (val.length > 2) {
            formatted = val.substring(0, 2) + '/' + val.substring(2);
        }

        // Append Year
        if (val.length > 4) {
            formatted = val.substring(0, 2) + '/' + val.substring(2, 4) + '/' + val.substring(4);
        }

        setManualDateInput(formatted);
    };

    const finishEditing = () => {
        const date = new Date(manualDateInput);
        if (!isNaN(date.getTime())) {
            setCurrentYear(date.getFullYear());
            setCurrentMonth(date.getMonth());
            setSelectedDate(date.getDate());
        }
        setIsEditingDate(false);
    };

    const handleDateClick = async (clickedDay) => {
        const currentUser = user || auth.currentUser;
        if (!currentUser) {
            if (setIsLoginOpen) setIsLoginOpen(true);
            alert("Please login to view or create journals.");
            return;
        }

        const monthStr = String(currentMonth + 1).padStart(2, '0');
        const dayStr = String(clickedDay).padStart(2, '0');
        const selectedDateStr = `${currentYear}-${monthStr}-${dayStr}`;

        try {
            const journalsRef = collection(db, "journals");
            const q = query(journalsRef, where("email", "==", currentUser.email));
            const snapshot = await getDocs(q);

            let existingData = null;
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.entry_time && data.entry_time.startsWith(selectedDateStr)) {
                    existingData = data;
                }
            });

            if (existingData) {
                navigate('/reflection-form', {
                    state: {
                        existingData: existingData,
                        dateStr: selectedDateStr,
                        formattedDate: `${clickedDay} ${monthName} ${currentYear}`
                    }
                });
            } else {
                navigate('/myreflectionbeforeclassify', { state: { dateStr: selectedDateStr } });
            }
        } catch (error) {
            console.error("Error fetching journals:", error);
            alert("Error checking journals.");
        }
    };

    return (
        <div className="ojs-container">
            <div className="ojs-content">
                <div className="ojs-header-container">
                    <button className="ojs-back-btn" onClick={() => navigate(-1)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>

                    <div className="ojs-logo-container">
                        <div className="ojs-sun-bg"></div>
                        <img
                            src="/reflections/nabilogo.png"
                            alt="Wings Logo"
                            className="ojs-logo-img"
                        />
                    </div>
                </div>

                <h1 className="ojs-main-title" style={{ fontWeight: 300 }}>My reflections</h1>

                <div className="ojs-top-cards">

                    <div className="ojs-calendar-modal" style={{ flex: 1, maxWidth: '450px', margin: '0', flexShrink: 0, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)', border: 'none', padding: '30px 20px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
                        <p className="ojs-calendar-subtitle">You can reflect on any day in your life</p>

                        <div className="ojs-calendar-header">
                            {isEditingDate ? (
                                <input
                                    type="text"
                                    className="ojs-date-edit-input"
                                    value={manualDateInput}
                                    onChange={handleTextInput}
                                    onBlur={finishEditing}
                                    onKeyDown={(e) => { if (e.key === 'Enter') finishEditing(); }}
                                    placeholder="MM/DD/YYYY"
                                    autoFocus
                                />
                            ) : (
                                <h2>{formattedDate}</h2>
                            )}
                            <svg onClick={startEditing} style={{ cursor: 'pointer' }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#003049" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9"></path>
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                            </svg>
                        </div>

                        <div className="ojs-calendar-divider"></div>

                        <div className="ojs-calendar-month-row">
                            <span className="ojs-calendar-month-name">{monthName} {currentYear} <span className="ojs-caret">▼</span></span>
                            <div className="ojs-calendar-arrows">
                                <span className="ojs-arrow" onClick={handlePrevMonth}>{'<'}</span>
                                <span className="ojs-arrow" onClick={handleNextMonth}>{'>'}</span>
                            </div>
                        </div>

                        <div className="ojs-calendar-grid">
                            <div className="ojs-calendar-day-name">S</div>
                            <div className="ojs-calendar-day-name">M</div>
                            <div className="ojs-calendar-day-name">T</div>
                            <div className="ojs-calendar-day-name">W</div>
                            <div className="ojs-calendar-day-name">T</div>
                            <div className="ojs-calendar-day-name">F</div>
                            <div className="ojs-calendar-day-name">S</div>

                            {[...Array(firstDay)].map((_, i) => (
                                <div key={`empty-${i}`} className="ojs-calendar-day empty"></div>
                            ))}
                            {[...Array(daysInMonth)].map((_, i) => {
                                const day = i + 1;
                                const cellDate = new Date(currentYear, currentMonth, day);
                                const isFuture = cellDate > today;

                                const monthStr = String(currentMonth + 1).padStart(2, '0');
                                const dayStr = String(day).padStart(2, '0');
                                const cellDateStr = `${currentYear}-${monthStr}-${dayStr}`;

                                const isJournaled = journaledDates.has(cellDateStr);

                                return (
                                    <div
                                        key={day}
                                        className={`ojs-calendar-day ${validSelectedDate === day ? 'selected' : ''} ${isFuture ? 'future' : ''} ${isJournaled ? 'journaled' : ''}`}
                                        onClick={() => {
                                            if (!isFuture) {
                                                setSelectedDate(day);
                                                handleDateClick(day);
                                            }
                                        }}
                                    >
                                        {day}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="ojs-calendar-legend" style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem', color: '#003049', marginTop: '15px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#f07167', display: 'inline-block' }}></span>
                                <span>Your past reflections </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#FF9D96', display: 'inline-block' }}></span>
                                <span>Reflections for today </span> 
                            </div>
                        </div>


                    </div>

                    <div className="ojs-previous-section">
                        {showFreqSetting ? (
                            <div className="ojs-frequency-card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '15px' }}>
                                    <h3 className="ojs-frequency-title" style={{ margin: 0 }}>How often do you want to journal</h3>
                                    <div className="ojs-info-icon-container">
                                        <div className="ojs-info-icon">i</div>
                                        <div className="ojs-info-tooltip">
                                            This is a one-time setup, pick a reminder time.You may change it at any time.
                                        </div>
                                    </div>
                                </div>

                                <div className="ojs-freq-options">
                                    {['Everyday', 'Once in 3 days', 'Once in 5 days', 'Once every week'].map((freq) => (
                                        <button
                                            key={freq}
                                            className={`ojs-freq-btn ${journalFreq === freq ? 'active' : ''}`}
                                            onClick={() => {
                                                handleFreqSelect(freq);
                                            }}
                                        >
                                            <span className={`ojs-heart-icon ${journalFreq === freq ? 'active-heart' : ''}`}>
                                                {journalFreq === freq ? (
                                                    <svg width="24" height="30" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <defs>
                                                            <linearGradient id="activeHeartGrad" x1="0%" y1="0%" x2="80%" y2="100%">
                                                                <stop offset="6.42%" stopColor="#FB5121" />
                                                                <stop offset="61.75%" stopColor="#FB9621" />
                                                                <stop offset="84.22%" stopColor="#FFFFFF" />
                                                            </linearGradient>
                                                        </defs>
                                                        <path transform="translate(0, 6)" fill="#eed9cb" d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" />
                                                        <path transform="translate(0, 3)" fill="#d2c4bb" d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" />
                                                        <path fill="url(#activeHeartGrad)" d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" />
                                                    </svg>
                                                ) : (
                                                    <svg width="24" height="30" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fill="#D4CBC6" d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className="ojs-freq-text">{freq}</span>
                                        </button>
                                    ))}
                                </div>

                            </div>
                        ) : (
                            <div className="ojs-frequency-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', textAlign: 'center' }}>
                                <div style={{ background: '#fdf3ed', color: '#A46D5B', padding: '20px 25px', borderRadius: '16px', marginBottom: '20px', width: '100%', boxSizing: 'border-box' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '1.15rem', fontWeight: '600' }}>You're all set! ✨</h4>
                                    <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                                        You have chosen to journal <strong>{journalFreq.toLowerCase()}</strong>.<br/>
                                        We'll make sure to gently remind you when it's time.
                                    </p>
                                </div>
                                <button 
                                    onClick={() => setShowFreqSetting(true)} 
                                    className="ojs-freq-btn" 
                                    style={{ padding: '12px 28px', background: '#fff', border: '2px solid #FFE6A7', borderRadius: '30px', cursor: 'pointer', color: '#003049', fontWeight: '600', transition: 'all 0.2s ease', fontSize: '0.9rem' }}
                                >
                                    Change Schedule
                                </button>
                            </div>
                        )}
                    </div>
                </div>


                <div className="ojs-footer-quote">
                    <p>Have the courage to follow your heart and intuition. They somehow know what you truly want to become.</p>
                </div>
            </div>

            {/* <img
                src="/mountains.png"
                alt="Mountains bottom background"
                className="ojs-bottom-bg-img"
                onError={(e) => e.target.style.display = 'none'}
            />  */}


        </div>
    );
}
